import { ref, onMounted, computed, defineComponent, Transition, toRefs, isVue2, inject, isVue3 } from "vue-demi";
import { createVNode, mergeProps, resolveComponent, isVNode } from "vue";
function useLazy(imageRef, offset) {
  const loaded = ref(false);
  const image2 = ref();
  onMounted(() => {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const imageDom = entry.target;
          imageDom.srcset = imageDom.dataset.srcset;
          imageObserver.unobserve(imageDom);
        }
      });
    }, {
      rootMargin: offset.value
    });
    imageObserver.observe(imageRef.value);
  });
  return { loaded, image: image2 };
}
function useResponsive(src, breakpoints, imageUrlGenerator, options = {}) {
  const srcset = computed(() => breakpoints.value.map((size) => {
    const url = imageUrlGenerator.value(src.value, Object.assign({
      width: size
    }, options));
    return `${url} ${size}w`;
  }).join(", "));
  return srcset;
}
const imageUrlGeneratorDefault = (url, options = {}) => url;
const defaultOptions = {
  quanlity: 0,
  format: "",
  lazy: false,
  placeholderLazyOffset: "0px",
  imageLazyOffset: "0px",
  responsive: false,
  progressive: false,
  breakpoints: [640, 768, 1024, 1280, 1536],
  sizes: "100vw",
  imageUrlGenerator: imageUrlGeneratorDefault,
  duration: 1,
  autoWebp: false
};
function useMergedOptions(props, globals = {}) {
  const mergedOptions = {};
  Object.keys(defaultOptions).forEach((key) => {
    var _a, _b, _c;
    mergedOptions[key] = (_c = ((_a = props[key]) == null ? void 0 : _a.value) ? props[key] : void 0) != null ? _c : ref((_b = globals[key]) != null ? _b : defaultOptions[key]);
  });
  return mergedOptions;
}
const imageBackground = "_image-background_18kuj_12";
const imagePlaceholderWrapper = "_image-placeholder-wrapper_18kuj_19";
const imagePlaceholder = "_image-placeholder_18kuj_19";
const placeholderLoaded = "_placeholder-loaded_18kuj_30";
const hasWebglFilter = "_has-webgl-filter_18kuj_30";
const image = "_image_18kuj_12";
const imageLoaded = "_image-loaded_18kuj_47";
var style = {
  "image-background": "_image-background_18kuj_12",
  imageBackground,
  "image-placeholder-wrapper": "_image-placeholder-wrapper_18kuj_19",
  imagePlaceholderWrapper,
  "image-placeholder": "_image-placeholder_18kuj_19",
  imagePlaceholder,
  "placeholder-loaded": "_placeholder-loaded_18kuj_30",
  placeholderLoaded,
  "has-webgl-filter": "_has-webgl-filter_18kuj_30",
  hasWebglFilter,
  image,
  "image-loaded": "_image-loaded_18kuj_47",
  imageLoaded
};
var ImageComponent = defineComponent({
  name: "ImageComponent",
  components: {
    Transition
  },
  props: {
    src: {
      type: String,
      default: ""
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    },
    quanlity: {
      type: Number,
      required: false
    },
    format: {
      type: String,
      required: false
    },
    lazy: {
      type: Boolean,
      default: false
    },
    placeholderLazyOffset: {
      type: String,
      required: false
    },
    imageLazyOffset: {
      type: String,
      required: false
    },
    responsive: {
      type: Boolean,
      required: false
    },
    progressive: {
      type: Boolean,
      required: false
    },
    breakpoints: {
      type: Array,
      required: false
    },
    sizes: {
      type: String,
      required: false
    },
    imageUrlGenerator: {
      type: Function,
      required: false
    },
    toGroup: {
      type: Function,
      required: false
    },
    duration: {
      type: Number,
      required: false
    },
    autoWebp: {
      type: Boolean,
      required: false
    }
  },
  emits: ["image-loaded", "placeholder-loaded", "image-error", "placeholder-error"],
  setup(props, ctx) {
    const {
      emit,
      root
    } = ctx;
    const {
      src
    } = toRefs(props);
    const globalOptions = isVue2 ? root.$asImageOptions : inject("$asImageOptions");
    const {
      responsive,
      progressive,
      lazy,
      placeholderLazyOffset,
      imageLazyOffset,
      breakpoints,
      duration,
      imageUrlGenerator,
      quanlity,
      format
    } = useMergedOptions(toRefs(props), globalOptions);
    const placeholder = ref();
    const placeholderLoaded2 = ref(false);
    const image2 = ref();
    const imageLoaded2 = ref(false);
    const ssrImageLoaded = ref(false);
    const ssrPlaceholderLoaded = ref(false);
    const placeholderSrcSet = progressive.value ? computed(() => imageUrlGenerator.value(src.value, {
      width: 48,
      blur: 2
    })) : src;
    const options = {};
    if (quanlity.value)
      options.quanlity = quanlity.value;
    if (format.value)
      options.format = format.value;
    const imageSrcSet = responsive.value ? useResponsive(src, breakpoints, imageUrlGenerator, options) : computed(() => imageUrlGenerator.value(src.value, options));
    const pictureOption = Object.assign({}, options, {
      format: "webp"
    });
    const pictureSrcSet = responsive.value ? useResponsive(src, breakpoints, imageUrlGenerator, pictureOption) : computed(() => imageUrlGenerator.value(src.value, pictureOption));
    if (lazy.value) {
      useLazy(image2, imageLazyOffset);
      if (progressive.value)
        useLazy(placeholder, placeholderLazyOffset);
    }
    const onImageLoaded = (m = false) => {
      emit("image-loaded");
      if (props.toGroup)
        props.toGroup(image2.value);
      imageLoaded2.value = true;
    };
    const onPlaceholderLoaded = (m = false) => {
      emit("placeholder-loaded");
      placeholderLoaded2.value = true;
    };
    return {
      placeholderSrcSet,
      imageLoaded: imageLoaded2,
      placeholderLoaded: placeholderLoaded2,
      imageSrcSet,
      pictureSrcSet,
      image: image2,
      placeholder,
      onImageLoaded,
      onPlaceholderLoaded,
      ssrImageLoaded,
      ssrPlaceholderLoaded,
      lazyRef: lazy,
      durationRef: duration
    };
  },
  render() {
    var _a, _b;
    const renderImg = (type = "image") => {
      var _a2, _b2;
      const isImage = type === "image";
      if ((isImage && !this.ssrImageLoaded || !isImage && !this.ssrPlaceholderLoaded) && ((_a2 = this.$parent) == null ? void 0 : _a2.$el) && !this.lazyRef) {
        const imgElement = (_b2 = this.$parent) == null ? void 0 : _b2.$el.querySelector(`[data-type=${type}]`);
        if (imgElement == null ? void 0 : imgElement.complete) {
          if (isImage) {
            this.ssrImageLoaded = true;
          } else {
            this.ssrPlaceholderLoaded = true;
          }
        }
      }
      const hasWebglFilter2 = isVue2 && this.$scopedSlots.webglfilter || !!this.$slots.webglfilter;
      let className = "";
      if (isImage) {
        if (hasWebglFilter2)
          className = `${style.image} ${style.hasWebglFilter}`;
        else
          className = style.image;
      } else {
        className = style.imagePlaceholder;
      }
      const imageLoaded2 = style.imageLoaded;
      const placeholderLoaded2 = style.placeholderLoaded;
      const attrs = {
        [this.lazyRef ? "data-srcset" : "srcset"]: isImage ? this.imageSrcSet : this.placeholderSrcSet,
        onload: `this.classList.add("${isImage ? imageLoaded2 : placeholderLoaded2}");`
      };
      if (hasWebglFilter2)
        attrs.crossorigin = "anonymous";
      const src = isVue2 ? {
        attrs
      } : attrs;
      return createVNode("img", mergeProps({
        "data-type": type,
        "importance": isImage ? "auto" : "high",
        "ref": type,
        "class": className,
        "style": {
          opacity: isImage && this.ssrImageLoaded || !isImage && this.ssrPlaceholderLoaded ? 1 : 0,
          transitionDuration: `${this.durationRef}s`
        },
        "width": this.width,
        "height": this.height
      }, src, {
        "sizes": this.sizes,
        "onLoad": isImage ? this.onImageLoaded : this.onPlaceholderLoaded
      }), null);
    };
    const renderPicture = () => {
      return this.autoWebp ? createVNode("picture", null, [createVNode("source", {
        "srcset": this.pictureSrcSet,
        "type": "image/webp"
      }, null), renderImg()]) : renderImg();
    };
    const renderWebglFilter = () => {
      var _a2, _b2, _c, _d;
      return isVue2 ? (_b2 = (_a2 = this.$scopedSlots).webglfilter) == null ? void 0 : _b2.call(_a2, {
        image: this.image
      }) : (_d = (_c = this.$slots).webglfilter) == null ? void 0 : _d.call(_c, {
        image: this.image
      });
    };
    return createVNode("div", {
      "class": "vue-awesome-image"
    }, [createVNode("div", {
      "class": style.imageBackground
    }, [isVue2 ? this.$slots.loading : (_b = (_a = this.$slots).loading) == null ? void 0 : _b.call(_a)]), createVNode("div", {
      "class": style.imagePlaceholderWrapper
    }, [renderImg("placeholder")]), renderPicture(), createVNode(resolveComponent("transition"), {
      "name": "fade"
    }, {
      default: () => [this.imageLoaded ? renderWebglFilter() : null]
    })]);
  }
});
function _isSlot(s) {
  return typeof s === "function" || Object.prototype.toString.call(s) === "[object Object]" && !isVNode(s);
}
var Image = defineComponent({
  name: "AsImage",
  components: {
    ImageComponent
  },
  inheritAttrs: false,
  setup() {
  },
  render() {
    if (isVue2) {
      let _slot;
      const loading = () => {
        return this.$slots.loading ? createVNode("template", {
          "slot": "loading"
        }, [this.$slots.loading]) : null;
      };
      return createVNode(ImageComponent, mergeProps({
        props: this.$attrs
      }, {
        on: this.$listeners
      }, {
        scopedSlots: {
          webglfilter: this.$scopedSlots.webglfilter
        }
      }), _isSlot(_slot = loading()) ? _slot : {
        default: () => [_slot]
      });
    } else {
      return createVNode(ImageComponent, this.$attrs, this.$slots);
    }
  }
});
Image.install = (app, options = {}) => {
  if (options.imageUrlGenerator) {
    if (isVue3) {
      app.provide("imageUrlGenerator", options.imageUrlGenerator);
      app.provide("$asImageOptions", options);
    } else {
      app.prototype.$imageUrlGenerator = options.imageUrlGenerator;
      app.prototype.$asImageOptions = options;
    }
  }
  app.component(Image.name, Image);
};
const AsImage = Image;
export { AsImage, AsImage as default };
